/**
 * Phase 16 (F8/CO3) — Compare Menus dialog.
 *
 * Side-by-side comparison of every menu in a restaurant's rotation.
 * Columns = each menu. Rows = union of all course names across the
 * rotation. Cells = dish names for that menu's course (or — if blank).
 * Top of each column: name + rating chip + comment.
 *
 * Optional "Sort by rating" toggle (Strong → Medium → Weak → Unrated).
 */
import { useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { BarChart3, ArrowDownUp } from "lucide-react";
import { cn } from "@/lib/utils";

const RATING_RANK = { strong: 0, medium: 1, weak: 2, null: 3 };
const RATING_CHIP = {
  strong: "border-emerald-300 bg-emerald-100 text-emerald-800",
  medium: "border-amber-300 bg-amber-100 text-amber-800",
  weak:   "border-rose-300 bg-rose-100 text-rose-800",
};

export default function CompareMenusDialog({
  open, onOpenChange, menus = [], slotLabel = "Menu rotation",
}) {
  const [sortByRating, setSortByRating] = useState(false);

  const sortedMenus = useMemo(() => {
    if (!sortByRating) return menus;
    return [...menus].sort((a, b) => {
      const ar = RATING_RANK[a?.rating ?? "null"] ?? 3;
      const br = RATING_RANK[b?.rating ?? "null"] ?? 3;
      return ar - br;
    });
  }, [menus, sortByRating]);

  // Union of courses preserving first-seen order across the rotation.
  const courseNames = useMemo(() => {
    const seen = new Set();
    const ordered = [];
    for (const m of (menus || [])) {
      for (const c of (m?.courses || [])) {
        const key = (c?.name || "").trim();
        if (!key) continue;
        if (!seen.has(key)) { seen.add(key); ordered.push(key); }
      }
    }
    return ordered;
  }, [menus]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl" lockOutsideClick>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-amber-600" />
            Compare Menus — {slotLabel}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">
              {sortedMenus.length} menus · {courseNames.length} courses
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setSortByRating((v) => !v)}
              className="h-7 text-[10px]"
              data-testid="compare-menus-sort-toggle"
            >
              <ArrowDownUp className="h-3 w-3" />
              {sortByRating ? "Sorted: Strong → Weak" : "Sort by rating"}
            </Button>
          </div>
          <div className="overflow-x-auto rounded border">
            <table className="min-w-full text-[11px]">
              <thead className="bg-slate-50">
                <tr>
                  <th className="sticky left-0 z-10 border-r bg-slate-50 px-2 py-2 text-left font-semibold">
                    Course
                  </th>
                  {sortedMenus.map((m) => (
                    <th
                      key={m.id || m.name}
                      className="min-w-[180px] border-r px-2 py-2 text-left align-top"
                    >
                      <div className="font-semibold">{m.name || "—"}</div>
                      {m.rating && (
                        <span
                          className={cn(
                            "mt-0.5 inline-block rounded border px-1 py-0.5 text-[9px] font-semibold uppercase",
                            RATING_CHIP[m.rating],
                          )}
                        >
                          {m.rating}
                        </span>
                      )}
                      {m.comment && (
                        <div className="mt-0.5 text-[10px] italic text-muted-foreground">
                          {m.comment}
                        </div>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {courseNames.length === 0 ? (
                  <tr>
                    <td colSpan={sortedMenus.length + 1} className="px-2 py-4 text-center text-muted-foreground">
                      No courses defined on any menu in this rotation yet.
                    </td>
                  </tr>
                ) : (
                  courseNames.map((cn_) => (
                    <tr key={cn_} className="border-t">
                      <td className="sticky left-0 z-10 border-r bg-white px-2 py-1.5 align-top font-medium">
                        {cn_}
                      </td>
                      {sortedMenus.map((m) => {
                        const course = (m?.courses || []).find(
                          (c) => (c?.name || "").trim() === cn_,
                        );
                        const dishes = course?.dishes || [];
                        return (
                          <td
                            key={(m.id || m.name) + "::" + cn_}
                            className="border-r px-2 py-1.5 align-top"
                          >
                            {dishes.length === 0 ? (
                              <span className="text-muted-foreground">—</span>
                            ) : (
                              <ul className="space-y-0.5">
                                {dishes.map((d, i) => (
                                  <li key={i}>{d}</li>
                                ))}
                              </ul>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
